import React from 'react'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import { useFormContext } from 'react-hook-form'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon'

import SearchFilterCoachMark from 'app/SearchPage/components/SearchFilterCoachMark'
import useSearchFilters from 'app/SearchPage/hooks/useSearchFilters'
import useSearchQuery from 'app/SearchPage/hooks/useSearchQuery'
import useSETrackEvent from 'app/SearchPage/hooks/useSETrackEvent'
import {
  useContentSearchFilter,
  useContentSearchTracker,
} from 'stores/content-search'

interface IQuickFiltersProps extends React.HTMLProps<HTMLDivElement> {}
const QuickFilters = ({ className = '', ...props }: IQuickFiltersProps) => {
  const router = useRouter()
  const { setValue, clearErrors } = useFormContext()
  const { hasSearchFiltersData, searchFiltersData } = useSearchFilters()
  const { searchQueryFromURL } = useSearchQuery()
  const contentSearchFilterStore = useContentSearchFilter()
  const contentSearchTrackerStore = useContentSearchTracker()
  const { SETrackSearchResultsPage } = useSETrackEvent()

  const selectedTopicsLength = React.useMemo(
    () => contentSearchFilterStore.selectedTopics.length,
    [contentSearchFilterStore.selectedTopics.length]
  )
  const selectedTopicsCountText = React.useMemo(
    () => (selectedTopicsLength > 0 ? `(${selectedTopicsLength})` : null),
    [selectedTopicsLength]
  )

  const handleQuickFilterClicked = (key) => {
    const selectedFormats = contentSearchFilterStore.selectedFormats.includes(
      key
    )
      ? contentSearchFilterStore.selectedFormats.filter((item) => item !== key)
      : [...contentSearchFilterStore.selectedFormats, key]

    setValue('searchQuery', searchQueryFromURL)
    clearErrors('searchQuery')
    SETrackSearchResultsPage('CLICK_SEARCH_QUICKFILTER', {
      filter: key,
      event_trigger: contentSearchTrackerStore.eventTrigger,
    })
    router.replace({
      pathname: '/pencarian',
      query: {
        q: searchQueryFromURL,
        ...(selectedFormats.length ? { formats: selectedFormats } : {}),
        ...(contentSearchFilterStore.selectedTopics.length
          ? { topics: contentSearchFilterStore.selectedTopics }
          : {}),
      },
    })
  }

  const handleOtherCategoriesFilterClicked = () => {
    contentSearchFilterStore.setSheetOpen(true)
    SETrackSearchResultsPage('CLICK_SEARCH_FILTER_OTHER_CATEGORIES', {
      event_trigger: contentSearchTrackerStore.eventTrigger,
    })
  }

  if (!hasSearchFiltersData) {
    return null
  }

  return (
    <div {...props} className={clsx('px-4 pb-4 mt-2 bg-white', className)}>
      <div
        id="coachmark-quick-filters"
        className="flex flex-wrap gap-2 rounded-xl"
      >
        {(searchFiltersData?.formats?.filter ?? []).map(
          ({ label = '', key = '' }) => {
            if (!label || !key) return null

            const isActive =
              contentSearchFilterStore.checkSelectedFormatStatus(key)

            return (
              <Button
                key={`search-quick-filter-${key.toLowerCase()}`}
                className={clsx(
                  '!rounded-full',
                  isActive &&
                    '!outline-blue-70 !bg-blue-5 !text-interactive-default'
                )}
                color="white"
                size="md"
                variant="solid"
                onClick={(e) => {
                  e.currentTarget.blur()
                  handleQuickFilterClicked(key)
                }}
              >
                {label}
              </Button>
            )
          }
        )}

        <Button
          color="white"
          size="md"
          variant="solid"
          className={clsx(
            '!rounded-full',
            selectedTopicsLength > 0 &&
              '!outline-blue-70 !bg-blue-5 !text-interactive-default'
          )}
          onClick={handleOtherCategoriesFilterClicked}
        >
          Kategori lainnya {selectedTopicsCountText}
          <Icon color="inherit">expand_more</Icon>
        </Button>

        <SearchFilterCoachMark />
      </div>
    </div>
  )
}

export default QuickFilters
